import { useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { cacheQueryData, getCachedQueryData } from '../utils/scoreQueue'
import { fetchTickerEvents, insertTickerEvents } from '../api/ticker'

let channelCounter = 0

/**
 * Query hook to fetch ticker events for a match.
 */
export function useTickerEvents(matchId) {
  const cacheKey = `tickerEvents_${matchId}`
  return useQuery({
    queryKey: ['tickerEvents', matchId],
    queryFn: async () => {
      const data = await fetchTickerEvents(matchId)
      cacheQueryData(cacheKey, data)
      return data
    },
    enabled: !!matchId,
    initialData: () => (matchId ? getCachedQueryData(cacheKey) : undefined),
    initialDataUpdatedAt: 0,
  })
}

/**
 * Mutation hook to emit (upsert) ticker events.
 */
export function useEmitTickerEvents(matchId) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: insertTickerEvents,
    onSuccess: () => {
      if (matchId) {
        queryClient.invalidateQueries({ queryKey: ['tickerEvents', matchId] })
      }
    },
  })
}

/**
 * Realtime subscription for ticker events on a match.
 * Invalidates the query cache when new events are inserted.
 * Optionally calls onNewEvent with the raw payload for toast alerts.
 */
export function useRealtimeTicker(matchId, onNewEvent) {
  const queryClient = useQueryClient()
  const channelIdRef = useRef(++channelCounter)

  useEffect(() => {
    if (!matchId) return

    const channel = supabase
      .channel(`ticker:${matchId}:${channelIdRef.current}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ticker_events',
          filter: `match_id=eq.${matchId}`,
        },
        (payload) => {
          queryClient.invalidateQueries({ queryKey: ['tickerEvents', matchId] })
          if (onNewEvent && payload.new) {
            onNewEvent(payload.new)
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [matchId, queryClient, onNewEvent])
}
